import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

// --- Types ---

interface BrandingData {
  logo_base64: string | null;
  banner_base64: string | null;
  primary_color: string;
  secondary_color: string;
  landing_sections: string | null;
}

interface BrandingResponse {
  data: BrandingData;
}

// --- Component ---

export default function BrandingPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [logo, setLogo] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#1e40af');
  const [secondaryColor, setSecondaryColor] = useState('#9333ea');
  const [landingSections, setLandingSections] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // --- Query ---

  const { data: brandingData, isLoading } = useQuery({
    queryKey: ['company-branding'],
    queryFn: () => api.get<BrandingResponse>('/v1/companies/me/branding'),
  });

  // Sync form state from fetched data
  useEffect(() => {
    if (brandingData?.data) {
      const b = brandingData.data;
      setLogo(b.logo_base64);
      setBanner(b.banner_base64);
      setPrimaryColor(b.primary_color || '#1e40af');
      setSecondaryColor(b.secondary_color || '#9333ea');
      setLandingSections(b.landing_sections ? JSON.stringify(JSON.parse(b.landing_sections), null, 2) : '');
    }
  }, [brandingData]);

  // --- Mutation ---

  const saveMutation = useMutation({
    mutationFn: (data: Partial<BrandingData>) =>
      api.patch('/v1/companies/me/branding', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-branding'] });
      setToast({ show: true, message: 'Personalizacion guardada exitosamente', type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: 'Error al guardar la personalizacion', type: 'error' });
    },
  });

  // --- Handlers ---

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string | null) => void,
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToast({ show: true, message: 'Solo se permiten archivos de imagen', type: 'error' });
      return;
    }

    // Validate size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setToast({ show: true, message: 'La imagen no puede superar los 2MB', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setter(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const validateJson = (value: string): boolean => {
    if (!value.trim()) {
      setJsonError(null);
      return true;
    }
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch {
      setJsonError('JSON invalido. Revisa la sintaxis.');
      return false;
    }
  };

  const handleSave = (): void => {
    if (landingSections.trim() && !validateJson(landingSections)) {
      return;
    }

    saveMutation.mutate({
      logo_base64: logo,
      banner_base64: banner,
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      landing_sections: landingSections.trim() || null,
    });
  };

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Personalizacion</h1>
          <p className="text-sm text-base-content/60">
            Configura la marca y apariencia de tu catalogo publico
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <span className="loading loading-spinner loading-sm" />
          ) : (
            'Guardar cambios'
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: Form */}
        <div className="space-y-6">
          {/* Logo upload */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Logo</h2>
              <p className="text-sm text-base-content/60">Imagen cuadrada recomendada, max 2MB</p>
              <div className="flex items-center gap-4 mt-3">
                {logo ? (
                  <div className="relative">
                    <img
                      src={logo}
                      alt="Logo"
                      className="w-20 h-20 object-contain rounded-lg border border-base-300"
                    />
                    <button
                      className="btn btn-circle btn-xs btn-error absolute -top-2 -right-2"
                      onClick={() => setLogo(null)}
                    >
                      &#10005;
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 border-2 border-dashed border-base-300 rounded-lg flex items-center justify-center text-base-content/30">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                    </svg>
                  </div>
                )}
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, setLogo)}
                  />
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    Subir logo
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Banner upload */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Banner</h2>
              <p className="text-sm text-base-content/60">Imagen horizontal recomendada (1200x300), max 2MB</p>
              <div className="mt-3">
                {banner ? (
                  <div className="relative">
                    <img
                      src={banner}
                      alt="Banner"
                      className="w-full h-32 object-cover rounded-lg border border-base-300"
                    />
                    <button
                      className="btn btn-circle btn-xs btn-error absolute top-2 right-2"
                      onClick={() => setBanner(null)}
                    >
                      &#10005;
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-32 border-2 border-dashed border-base-300 rounded-lg flex items-center justify-center text-base-content/30">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                    </svg>
                  </div>
                )}
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, setBanner)}
                />
                <button
                  className="btn btn-outline btn-sm mt-3"
                  onClick={() => bannerInputRef.current?.click()}
                >
                  Subir banner
                </button>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Colores</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Color primario</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      className="w-10 h-10 rounded cursor-pointer border border-base-300"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                    <input
                      type="text"
                      className="input input-bordered input-sm flex-1"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="#1e40af"
                    />
                  </div>
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Color secundario</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      className="w-10 h-10 rounded cursor-pointer border border-base-300"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                    />
                    <input
                      type="text"
                      className="input input-bordered input-sm flex-1"
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      placeholder="#9333ea"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Landing Sections JSON */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Secciones del catalogo</h2>
              <p className="text-sm text-base-content/60">
                Define secciones personalizadas en formato JSON para tu pagina de catalogo
              </p>
              <div className="form-control mt-2">
                <textarea
                  className={`textarea textarea-bordered w-full font-mono text-sm ${jsonError ? 'textarea-error' : ''}`}
                  rows={8}
                  placeholder='[\n  {\n    "title": "Bienvenidos",\n    "content": "Texto personalizado..."\n  }\n]'
                  value={landingSections}
                  onChange={(e) => {
                    setLandingSections(e.target.value);
                    if (jsonError) validateJson(e.target.value);
                  }}
                  onBlur={() => validateJson(landingSections)}
                />
                {jsonError && (
                  <label className="label">
                    <span className="label-text-alt text-error">{jsonError}</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Preview */}
        <div className="space-y-6">
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Vista previa del catalogo</h2>
              <p className="text-sm text-base-content/60">
                Asi se vera el encabezado de tu catalogo publico
              </p>

              {/* Preview mock */}
              <div className="mt-4 border border-base-300 rounded-lg overflow-hidden">
                {/* Banner area */}
                <div
                  className="h-32 relative flex items-end"
                  style={{
                    backgroundColor: banner ? undefined : secondaryColor,
                    backgroundImage: banner ? `url(${banner})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </div>

                {/* Logo + nav area */}
                <div className="px-4 py-3" style={{ backgroundColor: primaryColor }}>
                  <div className="flex items-center gap-3">
                    {logo ? (
                      <img
                        src={logo}
                        alt="Logo preview"
                        className="w-10 h-10 object-contain rounded bg-white/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-white/20 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">LOGO</span>
                      </div>
                    )}
                    <div>
                      <p className="text-white font-bold text-sm">Tu Inmobiliaria</p>
                      <p className="text-white/70 text-xs">catalogo de propiedades</p>
                    </div>
                  </div>
                </div>

                {/* Content area mock */}
                <div className="p-4 bg-base-200">
                  <div className="flex gap-2 mb-3">
                    <div className="h-2 rounded-full flex-1" style={{ backgroundColor: primaryColor, opacity: 0.3 }} />
                    <div className="h-2 rounded-full flex-1 bg-base-300" />
                    <div className="h-2 rounded-full flex-1 bg-base-300" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-16 rounded bg-base-300" />
                    <div className="h-16 rounded bg-base-300" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Color swatches */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h2 className="card-title text-lg">Paleta de colores</h2>
              <div className="flex gap-4 mt-2">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className="w-16 h-16 rounded-lg border border-base-300"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-xs text-base-content/60">Primario</span>
                  <span className="text-xs font-mono">{primaryColor}</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div
                    className="w-16 h-16 rounded-lg border border-base-300"
                    style={{ backgroundColor: secondaryColor }}
                  />
                  <span className="text-xs text-base-content/60">Secundario</span>
                  <span className="text-xs font-mono">{secondaryColor}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{toast.message}</span>
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
